import { api } from "boot/axios";
import QuickLRU from "quick-lru";
import memoize from "memoizee";

const stockCountCache = new QuickLRU({
  maxSize: 100,
  maxAge: 60 * 1000,
});
const highLowAvgCache = new QuickLRU({
  maxSize: 100,
  maxAge: 60 * 1000,
});

export default () => {
  const priceHistoryEOD = async (date) => {
    try {
      const data = await api.get(`/data/v2/price-history/eod/?date=${date}`);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const dashboardCount = async () => {
    let id = "count";
    if (stockCountCache.has(id)) {
      return stockCountCache.get(id);
    }
    try {
      const data = await api.get(`/data/v2/stocks-count/`);
      stockCountCache.set(id, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const floorsheetByDate = async (date, page, size) => {
    try {
      const data = await api.get("/data/v2/floorsheet/bydate/", {
        params: {
          date: date,
          page: page,
          size: size,
        },
      });
      return data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  };

  const floorsheetBySymbol = async (symbol, date, page, size) => {
    try {
      const data = await api.get("/data/v2/floorsheet/bysymbol/", {
        params: {
          symbol: symbol,
          date: date,
          page: page,
          size: size,
        },
      });
      return data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  };

  const floorsheetSum = async (symbol, date, target = null, broker = null) => {
    try {
      let data;
      if (target && broker) {
        data = await api.get("/data/v2/floorsheet/sum/", {
          params: {
            symbol: symbol,
            date: date,
            target: target,
            broker: broker,
          },
        });
      } else {
        data = await api.get("/data/v2/floorsheet/sum/", {
          params: {
            symbol: symbol,
            date: date,
          },
        });
      }
      return data;
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  };

  const marketSummaryTopTransactionCount = async () => {
    try {
      const data = await api.get(`/data/v2/market-summary/top-transaction/`);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  //marketSummary v2
  const marketSummaryV2 = async (type) => {
    try {
      const data = await api.get(`/data/v2/market-summary/?type=${type}`);
      return data;
    } catch (error) {
      throw error;
    }
  };

  //volatile v2
  const volatile = async () => {
    try {
      const data = await api.get(`/data/v2/volatile/`);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const dynamicMarketSummary = async (fromDate, toDate) => {
    try {
      const data = await api.get(
        `/data/v2/market-summary/bydate/?from_date=${fromDate}&to_date=${toDate}`
      );
      return data;
    } catch (error) {
      throw error;
    }
  };

  const valueAtRisk = async () => {
    try {
      const data = await api.get(`/data/v2/var-monthly/`);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  };

  //HighLowAvg Symbol Count
  const HighLowAvgSymbolCount = memoize(
    async (symbol) => {
      if (highLowAvgCache.has(symbol)) {
        return highLowAvgCache.get(symbol);
      }

      try {
        const data = await api.get(
          `/data/v2/high-low-avg-count/?symbol=${symbol}`
        );
        highLowAvgCache.set(symbol, data);
        return data;
      } catch (error) {
        throw error;
      }
    },
    {
      maxAge: 60 * 1000,
      promise: true,
      normalizer: (args) => JSON.stringify(args),
    }
  );

  //Live Market Data
  const LiveMarketData = async () => {
    try {
      const data = await api.get(`/data/v2/live-market/`);
      return data;
    } catch (error) {
      throw error;
    }
  };
  const LiveMarketDataIntraday = async (symbol) => {
    try {
      let data;
      if (symbol) {
        data = await api.get(`/data/v2/live-market-intraday/`, {
          params: { symbol: symbol },
        });
      }
      return data;
    } catch (error) {
      throw error;
    }
  };

  const ltpVSClose = async () => {
    try {
      const data = await api.get(`/data/v3/ltp-close/`);
      return data;
    } catch (error) {
      throw error;
    }
  };
  const liveWeakBuy = async () => {
    try {
      const data = await api.get(`/data/v2/weak/buy/`);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const liveWeakSell = async () => {
    try {
      const data = await api.get(`/data/v2/weak/sell/`);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const marketDepthTotal = async (symbol) => {
    try {
      const data = await api.get(
        `/data/v2/market-depth-total/?symbol=${symbol}`
      );
      return data;
    } catch (error) {
      throw error;
    }
  };
  const marketDepth = async (symbol) => {
    try {
      const data = await api.get(`/data/v2/market-depth/?symbol=${symbol}`);
      return data;
    } catch (error) {
      throw error;
    }
  };

  return {
    dynamicMarketSummary,
    valueAtRisk,
    priceHistoryEOD,
    dashboardCount,
    floorsheetByDate,
    floorsheetBySymbol,
    floorsheetSum,
    marketSummaryTopTransactionCount,
    marketSummaryV2,
    HighLowAvgSymbolCount,
    LiveMarketData,
    LiveMarketDataIntraday,
    volatile,
    ltpVSClose,
    liveWeakBuy,
    liveWeakSell,
    marketDepth,
    marketDepthTotal,
  };
};
